import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Circle, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const OWNER = { owner: ["You", "bg-orange-500 text-black"], dev: ["Dev team", "bg-sky-400/15 text-sky-200 border border-sky-400/30"], both: ["Both", "bg-white/10 text-stone-200 border border-white/20"] };
const PRIO = { P0: "text-red-300 border-red-400/40", P1: "text-amber-200 border-amber-400/40", P2: "text-stone-300 border-white/20" };
const DSTATUS = { pending: "Pending", approved: "Approved", needs_change: "Needs change", missing: "Missing" };
const card = "rounded-2xl border border-white/10 bg-[#141110]";
const when = (v) => (v ? v.slice(0, 16).replace("T", " ") : "");
// Launch gate wording: READY = done and tested; BLOCKED = open P0 (must be resolved before publication); PENDING = other open items.
const gateOf = (t) => (t.done ? "READY" : t.priority === "P0" ? "BLOCKED" : "PENDING");
const GATE = { READY: "text-emerald-300 border-emerald-400/40", PENDING: "text-amber-200 border-amber-400/40", BLOCKED: "text-red-300 border-red-400/50" };

const GateBanner = ({ tasks }) => {
  const blocked = tasks.filter((t) => gateOf(t) === "BLOCKED").length;
  return (
    <div className={`rounded-2xl border px-5 py-4 ${blocked ? "border-red-400/40 bg-red-500/10" : "border-emerald-400/40 bg-emerald-500/10"}`} data-testid="launch-gate">
      <p className="font-display text-lg text-stone-50" data-testid="launch-gate-status">Launch gate: {blocked ? `BLOCKED (${blocked} item${blocked === 1 ? "" : "s"})` : "CLEAR"}</p>
      <p className="mt-1 text-xs text-stone-300">Even when clear, the site is published only after your separate, explicit final launch authorisation.</p>
    </div>
  );
};

const Summary = ({ tasks }) => {
  const open = tasks.filter((t) => !t.done);
  const items = [["Open tasks", open.length, "open"], ["Open P0 (launch blockers)", open.filter((t) => t.priority === "P0").length, "p0"],
    ["Waiting on you", open.filter((t) => t.owner !== "dev").length, "owner"], ["Dev team", open.filter((t) => t.owner === "dev").length, "dev"],
    ["Done", tasks.length - open.length, "done"]];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
      {items.map(([l, v, id]) => (
        <div key={id} className={`${card} p-4`} data-testid={`launch-stat-${id}`}>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">{l}</p>
          <p className="mt-2 font-display text-2xl text-stone-50">{v}</p>
        </div>
      ))}
    </div>
  );
};

const TaskRow = ({ t, onToggle, busy }) => {
  const [label, cls] = OWNER[t.owner];
  return (
    <li className="flex flex-col gap-3 border-b border-white/10 px-5 py-4 sm:flex-row sm:items-start" data-testid={`launch-task-${t.id}`}>
      <button type="button" disabled={t.auto || busy} onClick={() => onToggle(t)} aria-pressed={t.done} data-testid={`launch-task-toggle-${t.id}`}
        aria-label={t.auto ? "Checked automatically" : t.done ? "Mark as not done" : "Mark as done"}
        className="mt-0.5 shrink-0 text-stone-400 enabled:hover:text-orange-400 disabled:cursor-default">
        {t.done ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : t.auto ? <Lock className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className={`text-sm ${t.done ? "text-stone-400 line-through" : "text-stone-100"}`}>{t.title}</p>
        <p className="mt-1 text-xs text-stone-400">
          {t.area} · see <span className="font-mono">{t.reference}</span>{t.auto ? " · live check" : ""}
          {t.detail ? <span className="block text-stone-300" data-testid={`launch-task-detail-${t.id}`}>{t.detail}</span> : null}
          {t.updated_by ? <span className="block">Marked by {t.updated_by} · {when(t.updated_at)}</span> : null}
        </p>
      </div>
      <div className="flex shrink-0 gap-2">
        <span className={`rounded-full border px-2.5 py-1 font-mono text-[11px] ${GATE[gateOf(t)]}`} data-testid={`launch-task-gate-${t.id}`}>{gateOf(t)}</span>
        <span className={`rounded-full border px-2.5 py-1 font-mono text-[11px] ${PRIO[t.priority]}`}>{t.priority}</span>
        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${cls}`} data-testid={`launch-task-owner-${t.id}`}>{label}</span>
      </div>
    </li>
  );
};

const DetailRow = ({ d, onSaved }) => {
  const [value, setValue] = useState(d.value ?? "");
  const [status, setStatus] = useState(d.status);
  const [note, setNote] = useState(d.note ?? "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/launch/company-details/${d.key}`, { value, status, note }); toast.success("Saved. Nothing has been published."); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const input = "h-10 w-full rounded-xl border border-white/10 bg-[#0C0A09] px-3 text-sm text-stone-100 outline-none focus:border-orange-500";
  return (
    <div className="grid gap-3 border-b border-white/10 px-5 py-4 lg:grid-cols-[1.1fr_1.6fr_0.8fr_1fr_auto] lg:items-end" data-testid={`company-detail-${d.key}`}>
      <div><p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">{d.category}</p><p className="mt-1 text-sm text-stone-100">{d.label}</p>
        <span className={`mt-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${d.public ? "bg-emerald-400/15 text-emerald-200" : "bg-white/10 text-stone-300"}`} data-testid={`company-detail-public-${d.key}`}>{d.public ? "On website" : "Not public"}</span>
        {d.updated_by !== "seed" && <p className="mt-1 text-[11px] text-stone-400">{d.updated_by} · {when(d.updated_at)}</p>}</div>
      <label className="block text-xs text-stone-400">Value<input className={`${input} mt-1`} value={value} onChange={(e) => setValue(e.target.value)} placeholder="Not supplied" maxLength={500} data-testid={`company-detail-value-${d.key}`} /></label>
      <label className="block text-xs text-stone-400">Decision
        <select className={`${input} mt-1`} value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`company-detail-status-${d.key}`}>
          {Object.entries(DSTATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select></label>
      <label className="block text-xs text-stone-400">Note<input className={`${input} mt-1`} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} data-testid={`company-detail-note-${d.key}`} /></label>
      <button type="button" onClick={save} disabled={busy} data-testid={`company-detail-save-${d.key}`}
        className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-orange-500 px-5 text-sm font-semibold text-black disabled:opacity-60">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
      </button>
    </div>
  );
};

export const LaunchDashboard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("open");
  const load = useCallback(async () => {
    try { const r = await api.get("/admin/launch"); setData(r.data ?? null); } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const tasks = useMemo(() => (Array.isArray(data?.tasks) ? data.tasks : []), [data]);
  const shown = tasks.filter((t) => (filter === "open" ? !t.done : filter === "owner" ? !t.done && t.owner !== "dev" : true));
  const toggle = async (t) => {
    setBusy(true);
    try { await api.put(`/admin/launch/tasks/${t.id}`, { done: !t.done }); await load(); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  if (!data) return <p className="mt-8 text-sm text-stone-400">Loading launch dashboard…</p>;
  return (
    <div className="mt-6 space-y-6" data-testid="admin-launch">
      <p className="rounded-2xl border border-amber-400/30 bg-amber-400/10 px-5 py-4 text-sm text-amber-100" data-testid="launch-not-published-banner">
        <strong>Not published.</strong> This dashboard tracks readiness only. It never deploys, publishes, changes DNS or deactivates accounts.
      </p>
      <GateBanner tasks={tasks} />
      <Summary tasks={tasks} />
      <section className={card}>
        <div className="flex flex-wrap items-center gap-2 border-b border-white/10 px-5 py-4">
          <h2 className="mr-auto font-display text-lg text-stone-50">Launch tasks <span className="ml-2 align-middle font-sans text-xs text-stone-400" data-testid="launch-filter-hint">{filter === "open" ? "Showing open tasks only" : filter === "owner" ? "Showing open tasks waiting on you" : "Showing all tasks"}</span></h2>
          {[["open", "Open"], ["owner", "Waiting on you"], ["all", "All"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} aria-pressed={filter === k} data-testid={`launch-filter-${k}`}
              className={`h-8 rounded-full px-3 text-xs ${filter === k ? "bg-white text-black" : "border border-white/15 text-stone-300"}`}>{l}</button>
          ))}
        </div>
        <ul>{["P0", "P1", "P2"].flatMap((p) => shown.filter((t) => t.priority === p)).map((t) => <TaskRow key={t.id} t={t} onToggle={toggle} busy={busy} />)}</ul>
        {shown.length === 0 && <p className="p-6 text-sm text-stone-400">Nothing here.</p>}
      </section>
      <section className={card} data-testid="company-details-checklist">
        <div className="border-b border-white/10 px-5 py-4">
          <h2 className="font-display text-lg text-stone-50">Company details approval</h2>
          <p className="mt-1 text-xs text-stone-400">Approving records your decision only. Approved details are added to the website and documents later, by the dev team, after you confirm. Nothing is published automatically.</p>
        </div>
        {(data.company_details ?? []).map((d) => <DetailRow key={`${d.key}-${d.updated_at}`} d={d} onSaved={load} />)}
      </section>
    </div>
  );
};
